import React, { forwardRef, useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";

import { FaPlay, FaPause, FaStepBackward, FaStepForward } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { HiVolumeUp, HiVolumeOff } from "react-icons/hi";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { closePlayer, RootState, setIsPlay } from "../../modules";
import { PRIMARY_COLOR } from "../../const";

import { MOBILE_MAX_WIDTH, BREAKPOINTS } from "./styles/const_style";

function formatTimeStamp(time: number): string {
    const minute = Math.floor(time / 60);
    const second = Math.floor(time % 60);
    return `${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;
}

const PlayerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 0;
    right: 0;
    border: none;
    outline: none;
    background: transparent;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s;
    opacity: 0.4;

    & svg {
        color: var(--text-color);
    }

    &:hover {
        opacity: 1;
        background: var(--gray-color);
    }
`;

const AlbumArt = styled.div`
    width: 200px;
    height: 200px;
    /* border-radius: 16px; */
    border-radius: 50%;
    background: linear-gradient(135deg, ${PRIMARY_COLOR}40 0%, ${PRIMARY_COLOR} 100%);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    margin-bottom: 24px;
    position: relative;
    cursor: pointer;
    user-select: none;

    & img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        width: 180px;
        height: 180px;
        margin-bottom: 20px;
        border-radius: 14px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        width: 140px;
        height: 140px;
        margin-bottom: 16px;
        border-radius: 12px;
    }
`;

const SeekIndicator = styled.div<{ $side: 'left' | 'right'; $visible: boolean }>`
    position: absolute;
    top: 50%;
    ${props => props.$side === 'left' ? 'left: 20%' : 'right: 20%'};
    transform: translateY(-50%);
    color: white;
    font-size: 1.2rem;
    font-weight: bold;
    opacity: ${props => props.$visible ? 1 : 0};
    transition: opacity 0.2s ease;
    pointer-events: none;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        font-size: 1rem;
    }
`;

const SongInfo = styled.div`
    text-align: center;
    margin-bottom: 24px;
    width: 100%;
    max-width: 300px;

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        max-width: 280px;
        margin-bottom: 20px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        max-width: 240px;
        margin-bottom: 16px;
    }
`;

const SongTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-color);
    margin: 0 0 8px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        font-size: 1rem;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        font-size: 0.9rem;
        margin: 0 0 6px 0;
    }
`;

const SongArtist = styled.p`
    font-size: 0.85rem;
    color: var(--text-color);
    opacity: 0.6;
    margin: 0 0 4px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        font-size: 0.8rem;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        font-size: 0.75rem;
    }
`;

const FolderTag = styled.span`
    font-size: 0.7rem;
    color: var(--text-color);
    opacity: 0.4;
`;

const ProgressSection = styled.div`
    width: 100%;
    max-width: 300px;
    margin-bottom: 20px;

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        max-width: 280px;
        margin-bottom: 16px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        max-width: 240px;
        margin-bottom: 12px;
    }
`;

interface SliderProps {
    $value: number;
    $isDragging?: boolean;
    $isPlaying?: boolean;
}

const ProgressBarContainer = styled.div<SliderProps>`
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: var(--gray-color);
    position: relative;
    cursor: pointer;
    overflow: hidden;

    @keyframes waveMove {
        0% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
`;

const ProgressFill = styled.div<SliderProps>`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => props.$value}%;
    background: var(--text-color);
    border-radius: 3px;
    transition: ${(props) => props.$isDragging ? 'none' : 'width 0.1s ease'};
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 20%,
            rgba(255, 255, 255, 0.5) 50%,
            rgba(255, 255, 255, 0.3) 80%,
            transparent 100%
        );
        animation: ${(props) => props.$isPlaying ? 'waveMove 1.5s ease-in-out infinite' : 'none'};
    }
`;

const ProgressHandle = styled.div<SliderProps>`
    position: absolute;
    top: 50%;
    left: ${(props) => props.$value}%;
    transform: translate(-50%, -50%) scale(${(props) => props.$isDragging ? 1 : 0});
    width: 14px;
    height: 14px;
    background: var(--text-color);
    border-radius: 50%;
    transition: transform 0.15s ease;
    box-shadow: ${(props) => props.$isPlaying ? '0 0 10px var(--btn-bg-color)' : '0 2px 4px rgba(0,0,0,0.2)'};
    z-index: 2;

    ${ProgressBarContainer}:hover & {
        transform: translate(-50%, -50%) scale(1);
    }
`;

const TimeStamps = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    color: var(--text-color);
    opacity: 0.5;
    margin-top: 8px;
`;

const ControlsSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const PlaybackControls = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

const ControlButton = styled.button`
    border: none;
    background: transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: all 0.15s;
    color: var(--text-color);
    opacity: 0.5;

    &:hover {
        opacity: 1;
    }

    &:active {
        transform: scale(0.95);
    }

    &:disabled {
        opacity: 0.2;
        cursor: not-allowed;
    }
`;

const PlayButton = styled.button`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--text-color);
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.15s;

    & svg {
        color: var(--bg-color);
    }

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    }

    &:active {
        transform: scale(0.98);
    }

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        width: 52px;
        height: 52px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        width: 48px;
        height: 48px;

        & svg {
            font-size: 1rem;
        }
    }
`;

const VolumeControl = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const VolumeButton = styled.button`
    border: none;
    background: transparent;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding: 4px;
    color: var(--text-color);
    opacity: 0.4;
    transition: opacity 0.15s;

    &:hover {
        opacity: 1;
    }
`;

const VolumeSlider = styled.div<SliderProps>`
    width: 80px;
    height: 3px;
    background: var(--gray-color);
    border-radius: 2px;
    position: relative;
    cursor: pointer;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: ${(props) => props.$value}%;
        background: var(--text-color);
        opacity: 0.5;
        transition: width 0.05s;
        border-radius: 2px;
    }

    @media screen and (max-width: ${MOBILE_MAX_WIDTH}) {
        width: 70px;
    }

    @media screen and (max-width: ${BREAKPOINTS.MOBILE}) {
        width: 60px;
    }
`;

interface CustomAudioProps {
    audioRef: React.RefObject<HTMLAudioElement>;
    onLoad: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
    onEnded: () => void;
    onTimeUpdate: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
    src: string;
}

const CustomAudio = ({audioRef, onLoad, onTimeUpdate, onEnded, src}: CustomAudioProps) => {
    return (
        <audio ref={audioRef} onLoadedMetadata={onLoad} onEnded={onEnded} onTimeUpdate={onTimeUpdate}>
            <source src={src} type="audio/mpeg" />
        </audio>
    );
};

const Player = forwardRef<HTMLDivElement>((_, ref) => {
    const dispatch = useDispatch();
    const { src, songInfo: song, isPlay, isLoading } = useSelector((state: RootState) => ({
        src: state.song.src,
        songInfo: state.song.songInfo,
        isPlay: state.song.isPlay,
        isLoading: state.loading.state
    }), shallowEqual);

    const [timestamp, setTimestamp] = useState({current: 0, duration: 0});
    const [volume, setVolume] = useState(100);
    const [isMuted, setIsMuted] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [seekIndicator, setSeekIndicator] = useState<{ side: 'left' | 'right'; visible: boolean }>({ side: 'left', visible: false });
    const isDraggingRef = useRef(false);
    const isSeekingRef = useRef(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const volumeRef = useRef<HTMLDivElement>(null);
    const albumArtRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        audioRef.current?.load();
    }, [src]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleEnded = () => {
            onStop();
        };

        const handleSeeking = () => {
            isSeekingRef.current = true;
        };

        const handleSeeked = () => {
            isSeekingRef.current = false;
        };

        audio.addEventListener("ended", handleEnded);
        audio.addEventListener("seeking", handleSeeking);
        audio.addEventListener("seeked", handleSeeked);

        return () => {
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("seeking", handleSeeking);
            audio.removeEventListener("seeked", handleSeeked);
        };
        // eslint-disable-next-line
    }, []);

    const onStop = () => {
        if (src === '') return;
        audioRef.current?.pause();
        if (audioRef.current) audioRef.current.currentTime = 0;
        setTimestamp({...timestamp, current: 0});
        dispatch(setIsPlay(false));
    };

    useEffect(() => {
        if (isLoading) {
            onStop();
        }
        // eslint-disable-next-line
    }, [isLoading]);

    const handlePlay = () => {
        if (src === '') return;
        audioRef.current?.play();
        dispatch(setIsPlay(true));
    };

    const handlePause = () => {
        if (src === '') return;
        audioRef.current?.pause();
        if (isPlay) dispatch(setIsPlay(false));
    };

    const handleClose = () => {
        dispatch(closePlayer());
    };

    // Double click to seek ±10 seconds
    const handleAlbumDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!albumArtRef.current || !audioRef.current || timestamp.duration === 0) return;

        const rect = albumArtRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const isLeftSide = clickX < rect.width / 2;

        const seekAmount = isLeftSide ? -10 : 10;
        const newTime = Math.max(0, Math.min(timestamp.duration, timestamp.current + seekAmount));

        audioRef.current.currentTime = newTime;
        setTimestamp(prev => ({ ...prev, current: newTime }));

        // Show indicator
        setSeekIndicator({ side: isLeftSide ? 'left' : 'right', visible: true });
        setTimeout(() => {
            setSeekIndicator(prev => ({ ...prev, visible: false }));
        }, 500);
    }, [timestamp.duration, timestamp.current]);

    const onTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
        // Don't update while dragging or seeking
        if (isDraggingRef.current || isSeekingRef.current) return;
        const target = e.target as HTMLAudioElement;
        if (target.currentTime === 0 && timestamp.duration === 0) return;
        setTimestamp({...timestamp, current: target.currentTime});
    };

    const onLoad = (e: React.SyntheticEvent<HTMLAudioElement>) => {
        const target = e.target as HTMLAudioElement;
        if (!target.duration || target.duration === 0) return;

        dispatch(setIsPlay(false));
        setTimestamp({current: 0, duration: target.duration});
    };

    // Progress bar seek with proper drag support
    const seekToPosition = useCallback((clientX: number) => {
        if (timestamp.duration === 0 || !sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        let percent = (clientX - rect.left) / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        const newTime = percent * timestamp.duration;

        setTimestamp(prev => ({...prev, current: newTime}));

        if (audioRef.current) {
            audioRef.current.currentTime = newTime;
        }
    }, [timestamp.duration]);

    const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        // 드래그 시작 시 현재 재생 상태 저장
        const wasPlaying = isPlay;

        isDraggingRef.current = true;
        setIsDragging(true);
        seekToPosition(e.clientX);

        const handleMouseMove = (moveEvent: MouseEvent) => {
            seekToPosition(moveEvent.clientX);
        };

        const handleMouseUp = () => {
            isDraggingRef.current = false;
            setIsDragging(false);

            // 드래그 전에 재생 중이었다면 다시 재생
            if (wasPlaying && audioRef.current) {
                audioRef.current.play();
            }

            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : volume / 100;
        }
    }, [volume, isMuted]);

    const handleVolumeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();

        const updateVolume = (clientX: number) => {
            if (volumeRef.current) {
                const rect = volumeRef.current.getBoundingClientRect();
                let newPercent = ((clientX - rect.left) / rect.width) * 100;
                newPercent = Math.max(0, Math.min(100, newPercent));
                setVolume(newPercent);
                if (newPercent > 0) setIsMuted(false);
            }
        };

        updateVolume(e.clientX);

        const onMouseMove = (moveEvent: MouseEvent) => {
            updateVolume(moveEvent.clientX);
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const skipForward = () => {
        if (audioRef.current && timestamp.duration > 0) {
            const newTime = Math.min(audioRef.current.currentTime + 10, timestamp.duration);
            audioRef.current.currentTime = newTime;
            setTimestamp({...timestamp, current: newTime});
        }
    };

    const skipBackward = () => {
        if (audioRef.current) {
            const newTime = Math.max(audioRef.current.currentTime - 10, 0);
            audioRef.current.currentTime = newTime;
            setTimestamp({...timestamp, current: newTime});
        }
    };

    const getTitle = useCallback((input: string) => {
        return input.replace(/\(.*?\)/g, "").trim();
    }, []);

    const getIndex = useCallback((input: string) => {
        const match = input.match(/(\(\d\))/);
        if (match) return match[1];
        else return '';
    }, []);

    const handleEnded = () => {
        if(audioRef.current) {
            dispatch(setIsPlay(false));
        }
    }

    const parseTitle = useCallback((input: string) => {
        if (!input) return input;
        const idx = getIndex(input);
        const title = getTitle(input);
        if (idx !== '') return `${title} ${idx}`;
        else return title;
    }, [getTitle, getIndex]);

    const progressValue = timestamp.duration !== 0 ? (timestamp.current / timestamp.duration) * 100 : 0;

    return (
        <PlayerContainer ref={ref}>
            {/* <CloseButton onClick={handleClose}>
                <IoMdClose size="1.2rem" />
            </CloseButton> */}

            <AlbumArt ref={albumArtRef} onDoubleClick={handleAlbumDoubleClick}>
                {song.imgSrc && <img src={song.imgSrc} alt="앨범 커버" />}
                <SeekIndicator $side="left" $visible={seekIndicator.side === 'left' && seekIndicator.visible}>
                    -10s
                </SeekIndicator>
                <SeekIndicator $side="right" $visible={seekIndicator.side === 'right' && seekIndicator.visible}>
                    +10s
                </SeekIndicator>
            </AlbumArt>

            <SongInfo>
                <SongTitle>{parseTitle(song.title)}</SongTitle>
                <SongArtist>{song.artist}</SongArtist>
                {song.folder && <FolderTag>{song.folder}</FolderTag>}
            </SongInfo>

            <ProgressSection>
                <ProgressBarContainer
                    ref={sliderRef}
                    onMouseDown={handleProgressMouseDown}
                    $value={progressValue}
                    $isDragging={isDragging}
                    $isPlaying={isPlay}
                >
                    <ProgressFill
                        $value={progressValue}
                        $isDragging={isDragging}
                        $isPlaying={isPlay}
                    />
                    <ProgressHandle
                        $value={progressValue}
                        $isDragging={isDragging}
                        $isPlaying={isPlay}
                    />
                </ProgressBarContainer>
                <TimeStamps>
                    <span>{formatTimeStamp(timestamp.current)}</span>
                    <span>{formatTimeStamp(timestamp.duration)}</span>
                </TimeStamps>
            </ProgressSection>

            <ControlsSection>
                <PlaybackControls>
                    <ControlButton onClick={skipBackward} disabled={!src}>
                        <FaStepBackward size="1rem" />
                    </ControlButton>
                    <PlayButton onClick={isPlay ? handlePause : handlePlay}>
                        {isPlay ?
                            <FaPause size="1.2rem" /> :
                            <FaPlay size="1.2rem" style={{ marginLeft: '3px' }} />
                        }
                    </PlayButton>
                    <ControlButton onClick={skipForward} disabled={!src}>
                        <FaStepForward size="1rem" />
                    </ControlButton>
                </PlaybackControls>

                <VolumeControl>
                    <VolumeButton onClick={toggleMute}>
                        {isMuted || volume === 0 ?
                            <HiVolumeOff size="1rem" /> :
                            <HiVolumeUp size="1rem" />
                        }
                    </VolumeButton>
                    <VolumeSlider
                        ref={volumeRef}
                        onMouseDown={handleVolumeMouseDown}
                        $value={isMuted ? 0 : volume}
                    />
                </VolumeControl>
            </ControlsSection>

            <CustomAudio
                audioRef={audioRef}
                onLoad={onLoad}
                onEnded={handleEnded}
                onTimeUpdate={onTimeUpdate}
                src={src}
            />
        </PlayerContainer>
    );
});

export default Player;
